import React, { useEffect, useState, useRef } from "react";
import Dashboard from "../components/Dashboard";
import Pagination from "../components/Pagination";
import PDFViewerPage from "../components/PdfViewerWithNotes";
import { FileUp, FileText } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import config from "../config";
import {
  ObjectPageHeader,
  PageContainer,
  FilterBar,
  Field,
  Select,
  SearchInput,
  FioriButton,
  FioriSwitch,
  StatusPill,
  ResponsiveTable,
  Spinner,
  EmptyState,
  Dialog,
  IconButton,
} from "../components/FioriUI";

const url = config.api.exceptions;

function O2CExceptions({ userEmail }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isLoadingFromURL = useRef(false);
  const [data, setData] = useState([]);
  const [auditTrailData, setAuditTrailData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    exceptionType: "",
    search: "",
    customer: "",
    processedTime: "",
  });
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [detailsModalExceptionId, setDetailsModalExceptionId] = useState(null);
  const [rowToRefresh, setRowToRefresh] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [exceptionTypeList, setExceptionTypeList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [view, setView] = useState("");
  const isDashboard = view === "dashboard";
  const [modal, setModal] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const toggleRow = (rowId) =>
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));

  // Auto-load PDF viewer when invoice_number is in URL
  useEffect(() => {
    const invoiceNumber = searchParams.get("invoice_number");
    if (
      invoiceNumber &&
      !showPDFViewer &&
      !isLoadingFromURL.current &&
      !loading
    ) {
      isLoadingFromURL.current = true;
      handleViewInvoicePDFFromURL(invoiceNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, data]);

  const handleViewInvoicePDFFromURL = async (invoiceNumber) => {
    try {
      const matchingRow = data.find(
        (row) => row.invoice_number === invoiceNumber,
      );
      if (!matchingRow) {
        setWarningMessage("Invoice not found.");
        setShowWarning(true);
        navigate("/o2c", { replace: true });
        return;
      }
      const s3Path = matchingRow.s3_pdf_path;
      if (!s3Path) {
        setWarningMessage("Invoice PDF is not available for this record.");
        setShowWarning(true);
        navigate("/o2c", { replace: true });
        return;
      }
      const response = await fetch(
        config.api.presignedUrl,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ s3_path: s3Path }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        if (data.presigned_url) {
          const s3PathFromResponse =
            data.bucket && data.key
              ? `s3://${data.bucket}/${data.key}`
              : s3Path;
          const invoiceData = {
            id: matchingRow.invoice_number || matchingRow.exception_id,
            invoice_number: matchingRow.invoice_number,
            invoiceDate: matchingRow.invoice_date
              ? new Date(matchingRow.invoice_date + "T00:00:00")
                  .toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                  .replace(/\//g, "-")
              : "-",
            amount: `USD ${matchingRow.amount || "-"}`,
            status: matchingRow.status || "UNKNOWN",
            pdfUrl: data.presigned_url,
            s3Path: s3PathFromResponse,
          };
          setSelectedInvoice(invoiceData);
          setShowPDFViewer(true);
        } else {
          setWarningMessage("Failed to generate PDF URL.");
          setShowWarning(true);
          navigate("/o2c", { replace: true });
        }
      } else {
        setWarningMessage("Failed to access PDF. Please try again.");
        setShowWarning(true);
        navigate("/o2c", { replace: true });
      }
    } catch (e) {
      console.error("Error fetching PDF from URL:", e);
      setWarningMessage("Error accessing PDF. Please try again.");
      setShowWarning(true);
      navigate("/o2c", { replace: true });
    }
  };

  // Initial load
  useEffect(() => {
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        const apiData = json.data || [];
        setData(apiData);
        setFilteredData(apiData);
        setLoading(false);
        setExceptionTypeList(
          Array.from(
            new Set(apiData.map((i) => i.exception_type).filter(Boolean)),
          ),
        );
        setCustomerList(
          Array.from(
            new Set(apiData.map((i) => i.customer_name).filter(Boolean)),
          ),
        );
        setStatusList(
          Array.from(new Set(apiData.map((i) => i.status).filter(Boolean))),
        );
      })
      .catch(() => setLoading(false));
  }, []);

  // Filtering
  useEffect(() => {
    let filtered = [...data];
    if (filters.status) {
      filtered = filtered.filter(
        (i) =>
          i.status && i.status.toUpperCase() === filters.status.toUpperCase(),
      );
    }
    if (filters.exceptionType) {
      filtered = filtered.filter(
        (i) => i.exception_type === filters.exceptionType,
      );
    }
    if (filters.customer) {
      filtered = filtered.filter((i) => i.customer_name === filters.customer);
    }
    if (filters.processedTime) {
      const now = new Date();
      filtered = filtered.filter((i) => {
        if (!i.updated_at) return false;
        const hours = (now - new Date(i.updated_at)) / (1000 * 60 * 60);
        if (filters.processedTime === "Last Day") return hours <= 24;
        if (filters.processedTime === "Last Week") return hours <= 24 * 7;
        if (filters.processedTime === "Last Month") return hours <= 24 * 30;
        return true;
      });
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          (i.exception_id &&
            String(i.exception_id).toLowerCase().includes(s)) ||
          i.invoice_number?.toLowerCase().includes(s) ||
          i.customer_name?.toLowerCase().includes(s) ||
          i.exception_type?.toLowerCase().includes(s),
      );
    }
    setFilteredData(filtered);
  }, [data, filters]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getValue = (row, key) => row[key] || "-";

  const renderActionDetails = (agentActions) => {
    if (!Array.isArray(agentActions) || agentActions.length === 0) {
      return <span className="text-fiori-text-disabled">-</span>;
    }
    return (
      <div className="space-y-1">
        {agentActions.map((detail, idx) => {
          const status = (detail.status || "").toLowerCase();
          const cls =
            status === "complete"
              ? "text-fiori-success font-medium"
              : status === "pending"
              ? "text-fiori-warning font-medium"
              : "text-fiori-text";
          return (
            <div key={idx} className={`text-sm ${cls}`}>
              • {detail.description}
            </div>
          );
        })}
      </div>
    );
  };

  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const handleClearFilters = () =>
    setFilters({
      status: "",
      exceptionType: "",
      search: "",
      customer: "",
      processedTime: "",
    });

  const handleRefreshData = () => {
    setLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        const apiData = json.data || [];
        setData(apiData);
        setFilteredData(apiData);
        setStatusList(
          Array.from(new Set(apiData.map((i) => i.status).filter(Boolean))),
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // Poll resolved row every 25s
  useEffect(() => {
    if (!rowToRefresh) return;
    const poll = async () => {
      try {
        const res = await fetch(url);
        const json = await res.json();
        const updated = (json.data || []).find(
          (i) => i.exception_id === rowToRefresh,
        );
        if (updated) {
          setData((prev) =>
            prev.map((i) =>
              i.exception_id === rowToRefresh ? updated : i,
            ),
          );
          setFilteredData((prev) =>
            prev.map((i) =>
              i.exception_id === rowToRefresh ? updated : i,
            ),
          );
          if (updated.status === "RESOLVED") setRowToRefresh(null);
        }
      } catch {
        /* ignore */
      }
    };
    const id = setInterval(poll, 25000);
    return () => clearInterval(id);
  }, [rowToRefresh]);

  const handleViewDetails = async (row) => {
    const exceptionId = row?.exception_id;
    setDetailsModalExceptionId(exceptionId);
    setDetailsError("");
    setDetailsLoading(true);
    setShowDetailsModal(true);

    try {
      const response = await fetch(
        `${config.api.auditTrails}?invoice_number=${row.invoice_number}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (response.ok) {
        const result = await response.json();
        const agentActions =
          result.agent_actions || result.body?.agent_actions;
        if (agentActions) {
          setAuditTrailData(agentActions);
          setSelectedInvoice(row);
        }
        setDetailsLoading(false);
      } else {
        setDetailsError("Failed to load action details.");
        setDetailsLoading(false);
      }
    } catch (e) {
      console.error("Error fetching agent actions:", e);
      setDetailsError("Error loading action details.");
      setDetailsLoading(false);
    }
  };

  const handleViewInvoicePDF = async (row) => {
    const s3Path = row.s3_pdf_path;
    if (!s3Path) {
      setWarningMessage("Invoice PDF is not available for this record.");
      setShowWarning(true);
      return;
    }
    navigate(`/o2c?invoice_number=${encodeURIComponent(row.invoice_number)}`, {
      replace: false,
    });
    isLoadingFromURL.current = true;

    try {
      const response = await fetch(
        config.api.presignedUrl,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ s3_path: s3Path }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        if (data.presigned_url) {
          const s3PathFromResponse =
            data.bucket && data.key ? `s3://${data.bucket}/${data.key}` : null;
          const invoiceData = {
            id: row.invoice_number || row.exception_id,
            invoiceDate: row.invoice_date
              ? new Date(row.invoice_date + "T00:00:00")
                  .toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                  .replace(/\//g, "-")
              : "-",
            amount: `USD ${row.amount || "-"}`,
            status: row.status || "UNKNOWN",
            pdfUrl: data.presigned_url,
            s3Path: s3PathFromResponse,
          };
          setSelectedInvoice(invoiceData);
          setShowPDFViewer(true);
        } else {
          setWarningMessage("Failed to generate PDF URL.");
          setShowWarning(true);
        }
      } else {
        setWarningMessage("Failed to access PDF. Please try again.");
        setShowWarning(true);
      }
    } catch (e) {
      console.error("Error fetching PDF:", e);
      setWarningMessage("Error accessing PDF. Please try again.");
      setShowWarning(true);
    }
  };

  const handleWarningClose = () => setShowWarning(false);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    if (isNaN(date)) return timestamp;
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <>
      {/* PDF Viewer Modal */}
      {showPDFViewer && selectedInvoice && (
        <PDFViewerPage
          invoice={selectedInvoice}
          userEmail={userEmail}
          onClose={() => {
            isLoadingFromURL.current = false;
            navigate("/o2c", { replace: false });
            setShowPDFViewer(false);
            setSelectedInvoice(null);
          }}
          onStatusUpdate={(updatedInvoice) => {
            setData((prev) =>
              prev.map((item) =>
                item.invoice_number === updatedInvoice.id ||
                item.invoice_number === updatedInvoice.invoice_number
                  ? { ...item, status: updatedInvoice.status }
                  : item,
              ),
            );
            setFilteredData((prev) =>
              prev.map((item) =>
                item.invoice_number === updatedInvoice.id ||
                item.invoice_number === updatedInvoice.invoice_number
                  ? { ...item, status: updatedInvoice.status }
                  : item,
              ),
            );
            setSelectedInvoice((prev) => ({
              ...prev,
              status: updatedInvoice.status,
            }));
          }}
        />
      )}

      <ObjectPageHeader
        title="O2C Exceptions Management"
        subtitle="Order to Cash invoice exceptions and audit trail"
        actions={
          <>
            <FioriSwitch
              checked={isDashboard}
              onChange={(v) => setView(v ? "dashboard" : "")}
              label="Dashboard"
            />
            <FioriButton
              variant="emphasized"
              onClick={handleRefreshData}
              disabled={loading}
              loading={loading}
            >
              {loading ? "Refreshing" : "Refresh"}
            </FioriButton>
          </>
        }
      />

      <PageContainer>
        {/* Upload toolbar */}
        <div className="bg-white border border-fiori-border-light rounded-fiori px-3 py-2 flex flex-wrap gap-2 items-center">
          <FioriButton
            variant="default"
            onClick={() => setModal("invoice")}
            icon={<FileUp className="w-4 h-4" />}
          >
            Upload Invoice
          </FioriButton>
          <FioriButton
            variant="default"
            onClick={() => setModal("support")}
            icon={<FileUp className="w-4 h-4" />}
          >
            Upload Remittance
          </FioriButton>
        </div>

        <FilterBar
          title="Filter Invoices"
          onClear={handleClearFilters}
          footer={
            <SearchInput
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search exception id, customer, or exception type"
            />
          }
        >
          <Field label="Status" htmlFor="status">
            <Select
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All</option>
              {statusList.map((s) => (
                <option key={s} value={s}>
                  {s
                    .toUpperCase()
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Exception Type" htmlFor="reasonCode">
            <Select
              id="reasonCode"
              value={filters.exceptionType}
              onChange={(e) =>
                handleFilterChange("exceptionType", e.target.value)
              }
            >
              <option value="">All</option>
              {exceptionTypeList.map((type) => (
                <option key={type} value={type}>
                  {type
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Processed Time" htmlFor="processedTime">
            <Select
              id="processedTime"
              value={filters.processedTime}
              onChange={(e) =>
                handleFilterChange("processedTime", e.target.value)
              }
            >
              <option value="">All</option>
              <option value="Last Day">Last Day</option>
              <option value="Last Week">Last Week</option>
              <option value="Last Month">Last Month</option>
            </Select>
          </Field>

          <Field label="Customer" htmlFor="customer">
            <Select
              id="customer"
              value={filters.customer}
              onChange={(e) => handleFilterChange("customer", e.target.value)}
            >
              <option value="">All</option>
              {customerList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        </FilterBar>

        {isDashboard ? (
          <Dashboard filteredData={filteredData} source="o2c" />
        ) : (
          <>
          <div className="flex justify-end mb-3">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
          <ResponsiveTable>
            <thead>
              <tr className="!bg-fiori-brand">
                <th className="whitespace-nowrap !bg-fiori-brand !text-white">Invoice #</th>
                <th className="whitespace-nowrap !bg-fiori-brand !text-white">Customer</th>
                <th className="whitespace-nowrap !bg-fiori-brand !text-white">Invoice Date</th>
                <th className="whitespace-nowrap !bg-fiori-brand !text-white">Amount</th>
                <th className="whitespace-nowrap !bg-fiori-brand !text-white">Due Date</th>
                <th className="whitespace-nowrap !bg-fiori-brand !text-white">Exception Type</th>
                <th className="min-w-[280px] !bg-fiori-brand !text-white">Last Action</th>
                <th className="!bg-fiori-brand !text-white">Status</th>
                <th className="text-center whitespace-nowrap !bg-fiori-brand !text-white">Audit Trail</th>
                <th className="text-center whitespace-nowrap !bg-fiori-brand !text-white">View Invoice</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10}>
                    <div className="flex justify-center items-center gap-3 py-8 text-fiori-text-secondary text-base">
                      <Spinner /> Loading exceptions
                    </div>
                  </td>
                </tr>
              ) : !Array.isArray(filteredData) || filteredData.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <EmptyState
                      title="No exceptions"
                      description="No invoices match the current filters."
                    />
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr key={idx} className="border-b border-fiori-border-light">
                    <td className="font-semibold text-base whitespace-nowrap py-5 px-6">
                      {getValue(row, "invoice_number")}
                    </td>
                    <td className="text-base py-5 px-6">{getValue(row, "customer_name")}</td>
                    <td className="whitespace-nowrap text-base py-5 px-6">
                      {row.invoice_date
                        ? new Date(row.invoice_date + "T00:00:00")
                            .toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                            .replace(/\//g, "-")
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap font-mono text-base py-5 px-6">
                      USD {getValue(row, "amount")}
                    </td>
                    <td className="whitespace-nowrap text-base py-5 px-6">
                      {row.due_date
                        ? new Date(row.due_date + "T00:00:00")
                            .toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                            .replace(/\//g, "-")
                        : "-"}
                    </td>
                    <td className="text-base py-5 px-6">
                      {getValue(row, "exception_type")
                        ?.replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </td>
                    <td className="py-5 px-6 min-w-[280px]">
                      {Array.isArray(row.agent_actions) &&
                      row.agent_actions.length > 0 ? (
                        <div className="border border-fiori-border-light rounded-fiori p-2 bg-fiori-page-2/40 max-w-md">
                          <div
                            className={
                              !expandedRows[row.dashboard_id]
                                ? "overflow-hidden max-h-[42px]"
                                : ""
                            }
                          >
                            {renderActionDetails(row.agent_actions)}
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleRow(row.dashboard_id)}
                            className="text-sm font-semibold text-fiori-brand hover:underline mt-1"
                          >
                            {expandedRows[row.dashboard_id]
                              ? "Show less"
                              : "Show more"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-fiori-text-disabled">-</span>
                      )}
                    </td>
                    <td className="py-5 px-6">
                      <StatusPill status={row.status} />
                    </td>
                    <td className="text-center py-5 px-6">
                      <IconButton
                        title="View Audit Trail"
                        onClick={() => handleViewDetails(row)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-6 h-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 12C2.25 12 5.25 6.75 12 6.75C18.75 6.75 21.75 12 21.75 12C21.75 12 18.75 17.25 12 17.25C5.25 17.25 2.25 12 2.25 12Z"
                          />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </IconButton>
                    </td>
                    <td className="text-center py-5 px-6">
                      <IconButton
                        title="View Invoice PDF"
                        tone="danger"
                        onClick={() => handleViewInvoicePDF(row)}
                      >
                        <FileText className="w-4 h-4" />
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
          </>
        )}
      </PageContainer>

      {/* Warning dialog */}
      <Dialog
        show={showWarning}
        title="Successfully Initiated"
        size="sm"
        onClose={handleWarningClose}
        footer={
          <FioriButton variant="emphasized" onClick={handleWarningClose}>
            OK
          </FioriButton>
        }
      >
        <div className="px-4 py-5 text-sm text-fiori-text">
          {warningMessage}
        </div>
      </Dialog>

      {/* Audit-trail dialog */}
      <Dialog
        show={showDetailsModal}
        title={`Invoice ${selectedInvoice?.invoice_number || ""}`}
        size="lg"
        onClose={() => {
          setShowDetailsModal(false);
          setDetailsModalExceptionId(null);
          setSelectedInvoice(null);
        }}
        footer={
          <FioriButton
            onClick={() => {
              setShowDetailsModal(false);
              setDetailsModalExceptionId(null);
              setSelectedInvoice(null);
            }}
          >
            Close
          </FioriButton>
        }
      >
        <div className="p-4">
          {detailsLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[150px] gap-3">
              <Spinner size="lg" />
              <p className="text-sm text-fiori-text-secondary">Loading...</p>
            </div>
          ) : detailsError ? (
            <p className="text-center text-fiori-error py-6">{detailsError}</p>
          ) : auditTrailData.length === 0 ? (
            <p className="text-center text-fiori-text-secondary py-8">
              No action details available for this exception.
            </p>
          ) : (
            <div className="border border-fiori-border-light rounded-fiori overflow-hidden">
              <table className="w-full border-collapse text-base">
                <thead>
                  <tr className="bg-fiori-brand">
                    <th className="text-left text-white font-semibold py-3 px-6 whitespace-nowrap">Timestamp</th>
                    <th className="text-left text-white font-semibold py-3 px-6">Status</th>
                    <th className="text-left text-white font-semibold py-3 px-6">Action Performed</th>
                  </tr>
                </thead>
                <tbody>
                  {auditTrailData.map((action, idx) => (
                    <tr key={idx} className="border-b border-fiori-border-light">
                      <td className="whitespace-nowrap font-mono text-base py-5 px-6">
                        {formatTimestamp(action.timestamp || action.created_at)}
                      </td>
                      <td className="py-5 px-6">
                        <StatusPill status={action.status} />
                      </td>
                      <td className="text-base py-5 px-6">{action.description || action.action || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Dialog>

      {/* Upload dialog */}
      {modal && (
        <UploadDialog type={modal} onClose={() => setModal(null)} />
      )}
    </>
  );
}

/* ─────────────────────────── UploadDialog (Fiori-styled) ─────────────────────────── */
function UploadDialog({ type, onClose }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [email, setEmail] = useState("");
  const [uploading, setUploading] = useState(false);

  const API_URL = config.api.generateUrl;

  const handleFiles = (files) => {
    const arr = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...arr]);
  };

  const handleConfirm = () => {
    const action = type === "invoice" ? "invoke_invoice" : "invoke_remittance";
    if (type === "invoice" && !email.trim()) {
      alert("Please enter an email address before uploading.");
      return;
    }
    setUploading(true);
    const uploads = selectedFiles.map((file) =>
      fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "generate_presigned_url",
          key: `sap-demo/edfr_o2c/${file.name}`,
          content_type: file.type,
        }),
      })
        .then((res) => res.json())
        .then((data) =>
          fetch(data.presigned_url, {
            method: "PUT",
            headers: { "Content-Type": file.type },
            body: file,
          }),
        )
        .then(() => {
          const body = {
            action,
            s3_path: `s3://karini-demo-data-us-east-1/sap-demo/edfr_o2c/${file.name}`,
          };
          if (type === "invoice") body.email = email;
          return fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
        }),
    );
    Promise.all(uploads)
      .then(() => onClose())
      .catch((err) => console.error("Upload failed:", err))
      .finally(() => setUploading(false));
  };

  return (
    <Dialog
      show
      title={type === "invoice" ? "Upload Invoice" : "Upload Remittance"}
      size="md"
      onClose={onClose}
      footer={
        <>
          <FioriButton onClick={onClose}>Cancel</FioriButton>
          <FioriButton
            variant="emphasized"
            onClick={handleConfirm}
            disabled={
              uploading ||
              selectedFiles.length === 0 ||
              (type === "invoice" && !email.trim())
            }
            loading={uploading}
          >
            {uploading ? "Uploading..." : "Upload"}
          </FioriButton>
        </>
      }
    >
      <div className="p-4 space-y-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`border-2 border-dashed rounded-fiori p-6 text-center cursor-pointer transition-colors ${
            dragging
              ? "border-fiori-brand bg-fiori-info-bg"
              : "border-fiori-border hover:border-fiori-brand bg-fiori-page-2/30"
          }`}
          onClick={() => document.getElementById(`file-input-${type}`).click()}
        >
          <FileUp
            className="mx-auto mb-2 text-fiori-brand"
            size={28}
          />
          <p className="text-sm text-fiori-text-secondary">
            Drag &amp; drop files here, or{" "}
            <span className="text-fiori-brand font-semibold">browse</span>
          </p>
          <input
            id={`file-input-${type}`}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {selectedFiles.length > 0 && (
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {selectedFiles.map((f, i) => (
              <li
                key={i}
                className="flex items-center justify-between text-sm text-fiori-text bg-fiori-page-2/50 px-3 py-1.5 rounded-fiori border border-fiori-border-light"
              >
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  className="ml-2 text-fiori-error hover:underline font-semibold"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFiles((prev) =>
                      prev.filter((_, idx) => idx !== i),
                    );
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {type === "invoice" && (
          <div>
            <label className="fiori-label" htmlFor="upload-email">
              Email <span className="text-fiori-error">*</span>
            </label>
            <input
              id="upload-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className="fiori-input"
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default O2CExceptions;
